import { ArrowDownToLine, ArrowUpFromLine, ArrowDown, ArrowUp, ClipboardCheck } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

/* Warna mengikuti token di tailwind.config.js */
export const COLORS = {
    ink: "#0A6CF0",
    inkSoft: "#7DB8FF",
    line: "#DCE4EE",
    track: "#E8EEF7",
    tag: "#F5A623"
};

export const formatNumber = (n) =>
    n === null || n === undefined || Number.isNaN(Number(n))
        ? "-"
        : Number(n).toLocaleString("id-ID");

/* ============ Kerangka panel ============ */

export function Panel({ className = "", children }) {
    return (
        <section className={`bg-white rounded-lg border border-line ${className}`}>
            {children}
        </section>
    );
}

export function PanelHeader({ title, meta, children }) {
    return (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 border-b border-line">
            <div>
                <h2 className="text-base font-semibold text-body">{title}</h2>
                {meta && <p className="text-xs text-muted mt-0.5">{meta}</p>}
            </div>
            {children && <div className="flex items-center gap-3">{children}</div>}
        </div>
    );
}

/* ============ Perubahan vs periode sebelumnya ============ */

function Trend({ value, suffix, decimals = 0, unit = "" }) {

    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return <div className="text-xs text-muted mt-2">&nbsp;</div>;
    }

    const num = Number(value);
    const flat = num === 0;
    const up = num > 0;
    const Icon = up ? ArrowUp : ArrowDown;

    return (
        <div className="flex items-center gap-1.5 text-xs mt-2">
            <span
                className={`inline-flex items-center gap-0.5 font-semibold rounded px-1.5 py-0.5 ${
                    flat
                        ? "bg-surface text-muted"
                        : up
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-orange-50 text-orange-700"
                }`}
            >
                {!flat && <Icon size={12} strokeWidth={2.5} />}
                {up ? "+" : ""}{num.toFixed(decimals)}{unit}
            </span>
            <span className="text-muted">{suffix}</span>
        </div>
    );
}

/* ============ Kartu angka ============ */

function StatTile({ icon: Icon, label, value, loading, children }) {
    return (
        <Panel className="p-5 h-full">
            <div className="flex items-center gap-2 text-sm text-muted">
                {Icon && <Icon size={16} className="text-ink" />}
                {label}
            </div>
            <div className="text-[34px] leading-none font-semibold text-body mt-3 tabular-nums">
                {loading ? <span className="text-line">···</span> : value}
            </div>
            {children}
        </Panel>
    );
}

export function FlowCard({ type = "inbound", value, trend, trendSuffix, loading = false }) {
    const isInbound = type === "inbound";

    return (
        <StatTile
            icon={isInbound ? ArrowDownToLine : ArrowUpFromLine}
            label={isInbound ? "Inbound" : "Outbound"}
            value={formatNumber(value)}
            loading={loading}
        >
            <Trend value={loading ? null : trend} suffix={trendSuffix} />
            <div className="text-xs text-muted mt-1">
                {isInbound ? "dokumen BBM masuk rak" : "batch keluar dari rak"}
            </div>
        </StatTile>
    );
}

export function StockAccuracyCard({ accuracy, change, matched, total, loading }) {

    const display =
        accuracy === null || accuracy === undefined
            ? "-"
            : `${Number(accuracy).toLocaleString("id-ID", { maximumFractionDigits: 2 })}%`;

    return (
        <StatTile
            icon={ClipboardCheck}
            label="Akurasi stok WMS × Oracle"
            value={display}
            loading={loading && (accuracy === null || accuracy === undefined)}
        >
            {change !== null && change !== undefined ? (
                <Trend value={change} suffix="vs minggu lalu" decimals={2} unit="%" />
            ) : (
                <div className="text-xs text-muted mt-2">
                    {formatNumber(matched)} dari {formatNumber(total)} item sesuai
                </div>
            )}
            <Meter value={Number(accuracy || 0)} className="mt-3" />
        </StatTile>
    );
}

/* ============ Meter horizontal ============ */

export function Meter({ value = 0, className = "" }) {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <div className={`h-1.5 rounded-full bg-[#E8EEF7] overflow-hidden ${className}`}>
            <div className="h-full bg-ink rounded-full" style={{ width: `${pct}%` }} />
        </div>
    );
}

/* ============ Kartu user login ============ */

export function UserLoginCard({ name, lastUpdated, loading, dueCount, onDueClick }) {
    return (
        <section className="bg-gradient-to-b from-[#1AA3FA] to-[#0A5FE6] text-white rounded-lg p-5 h-full flex flex-col">
            <div className="text-sm text-white/80">User login</div>

            <div className="flex items-center gap-3 mt-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-white text-ink font-bold text-lg flex items-center justify-center shrink-0">
                    {name && name !== "-" ? name.charAt(0).toUpperCase() : "?"}
                </div>
                <div className="text-xl font-semibold leading-tight truncate" title={name}>
                    {name}
                </div>
            </div>

            <div className="mt-4 text-sm text-white/80">Terakhir diperbarui</div>
            <div className="text-sm font-medium tabular-nums">
                {loading && !lastUpdated ? "···" : lastUpdated}
            </div>

            {dueCount > 0 && (
                <button
                    type="button"
                    onClick={onDueClick}
                    className="mt-auto pt-4 text-left text-sm font-semibold text-white underline underline-offset-2 decoration-white/50 hover:decoration-white"
                >
                    {formatNumber(dueCount)} batch jatuh tempo dalam 12 bulan
                </button>
            )}
        </section>
    );
}

/* ============ Kartu occupancy ============ */

export function OccupancyCard({ percent = 0, used = 0, total = 0, pallets = 0, totalRack = 0, loading }) {
    const pct = Number(percent || 0);
    const free = Math.max(Number(total) - Number(used), 0);

    return (
        <Panel className="p-5 h-full flex flex-col">
            <div className="text-sm text-muted">Occupancy rak</div>

            <div className="flex items-end gap-1.5 mt-3">
                <div className="text-[34px] leading-none font-semibold text-body tabular-nums">
                    {loading
                        ? <span className="text-line">···</span>
                        : pct.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-lg text-muted pb-0.5">%</div>
            </div>

            <Meter value={pct} className="mt-4 !h-2.5" />

            <dl className="grid grid-cols-3 gap-3 mt-5 text-sm">
                <div>
                    <dt className="text-xs text-muted">Slot terisi</dt>
                    <dd className="font-semibold tabular-nums">{formatNumber(used)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted">Slot kosong</dt>
                    <dd className="font-semibold tabular-nums">{formatNumber(free)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted">Total slot</dt>
                    <dd className="font-semibold tabular-nums">{formatNumber(total)}</dd>
                </div>
            </dl>

            <div className="flex-1 min-h-[20px]" />

            <div className="pt-4 border-t border-line flex justify-between text-xs text-muted">
                <span>{formatNumber(totalRack)} rak</span>
                <span>{formatNumber(pallets)} palet di rak</span>
            </div>
        </Panel>
    );
}

/* ============ Kartu pallet utilization (donut) ============
 * active    : palet master yang ada di rak (warehouse terpilih)
 * other     : palet master yang ada di rak warehouse lain
 * idle      : palet yang pernah dipakai, saat ini tidak di rak
 * neverUsed : palet master yang belum pernah dipakai (tidak masuk donut)
 */
export function PalletUtilizationCard({ active = 0, other = 0, idle = 0, neverUsed = 0, masterTotal = 0, percent: percentProp, loading = false }) {

    const total = active + other + idle;
    const percent = percentProp !== null && percentProp !== undefined
        ? Number(percentProp)
        : total > 0 ? (active / total) * 100 : 0;

    const segments = [
        { key: "active", name: "Di rak", value: active, color: COLORS.ink },
        { key: "other", name: "Di rak warehouse lain", value: other, color: COLORS.inkSoft },
        { key: "idle", name: "Tidak di rak", value: idle, color: COLORS.track }
    ];

    const data = total > 0
        ? segments.filter((s) => s.value > 0)
        : [{ key: "empty", name: "Kosong", value: 1, color: COLORS.track }];

    return (
        <Panel className="p-5 h-full flex flex-col">
            <div className="text-sm text-muted">Utilisasi palet</div>

            <div className="flex items-center gap-5 mt-3">
                <div className="relative w-32 h-32 shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="value"
                                innerRadius="74%"
                                outerRadius="100%"
                                startAngle={90}
                                endAngle={-270}
                                stroke="#fff"
                                strokeWidth={2}
                                isAnimationActive={false}
                            >
                                {data.map((d) => (
                                    <Cell key={d.key} fill={d.color} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>

                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <div className="text-xl font-semibold text-body leading-none tabular-nums">
                            {loading ? "···" : `${percent.toLocaleString("id-ID", { maximumFractionDigits: 1 })}%`}
                        </div>
                        <div className="text-[11px] text-muted mt-1">di rak</div>
                    </div>
                </div>

                <ul className="flex-1 space-y-2 text-sm min-w-0">
                    {segments
                        .filter((s) => s.key !== "other" || s.value > 0)
                        .map((s) => (
                            <li key={s.key} className="flex items-center justify-between gap-2">
                                <span className="flex items-center gap-2 text-muted min-w-0">
                                    <span
                                        className="w-2.5 h-2.5 rounded-sm shrink-0"
                                        style={{
                                            background: s.color,
                                            outline: s.key === "idle" ? `1px solid ${COLORS.line}` : "none"
                                        }}
                                    />
                                    <span className="truncate">{s.name}</span>
                                </span>
                                <span className="font-semibold text-body tabular-nums">{formatNumber(s.value)}</span>
                            </li>
                        ))}
                </ul>
            </div>

            <div className="flex-1 min-h-[20px]" />

            <div className="pt-4 border-t border-line text-xs text-muted flex flex-wrap justify-between gap-x-3 gap-y-1">
                <span>{formatNumber(total)} palet pernah dipakai</span>
                <span>
                    {formatNumber(neverUsed)} belum pernah dipakai
                    {masterTotal > 0 && <> dari {formatNumber(masterTotal)}</>}
                </span>
            </div>
        </Panel>
    );
}
